import relationApiService from '@/core/api/services/relationApiService';
import { analysisApiClient, getAnalysisMockScope } from '@/core/analysis/api/AnalysisApiClient';
import { normalizeRelationResponse } from '@/core/api/adapters/relationAdapter';

/**
 * Relation Gateway
 * 统一处理 Relation/History 相关 API 调用
 * Mock 逻辑已迁移至 AnalysisApiClient mock 系统
 */
const relationGateway = {
  async saveRelation(payload) {
    const response = await relationApiService.saveRelation(payload);
    return normalizeRelationResponse(response);
  },

  async fetchRelation(hashId, options = {}) {
    const response = await relationApiService.fetchRelation(hashId, options);
    return normalizeRelationResponse(response);
  },

  async listHistory({ params = {}, headers = {} } = {}) {
    console.log('[0.9.4.9] relationGateway listHistory called', {
      hasParams: Object.keys(params).length > 0,
      params,
      hasHeaders: Object.keys(headers).length > 0,
      hasFingerprintHeader: !!headers?.['X-Photoscope-If-Resource-Fingerprint'],
      fingerprintHeaderValue: headers?.['X-Photoscope-If-Resource-Fingerprint'] || null,
      isMockEnabled: analysisApiClient.isMockEnabled(),
      mockScope: getAnalysisMockScope()
    });

    // Mock 由 AnalysisApiClient 统一处理，但需要检查mock作用域
    // 只有在mock作用域为'all'时才对relation接口启用mock
    if (analysisApiClient.isMockEnabled() && getAnalysisMockScope() === 'all') {
      const incomingFingerprint = headers?.['X-Photoscope-If-Resource-Fingerprint'];
      const mockFingerprint = 'photos-history-v1:mock-fingerprint';

      console.log('[0.9.4.9] relationGateway mock mode activated', {
        incomingFingerprint,
        mockFingerprint,
        fingerprintMatch: incomingFingerprint === mockFingerprint
      });

      if (incomingFingerprint && incomingFingerprint === mockFingerprint) {
        const notModifiedResponse = {
          data: {
            cache_status: 'not_modified',
            fingerprint: mockFingerprint,
            last_modified: 'Mon, 04 May 2026 10:26:16 GMT',
            query_status: 'active',
            relation_count: 9
          },
          status: 200,
          headers: {
            'X-Photoscope-Resource-Fingerprint': mockFingerprint,
            'X-Photoscope-Cache-Status': 'not_modified'
          }
        };

        console.log('[0.9.4.9] relationGateway returning mock not_modified response', {
          cacheStatus: notModifiedResponse.data.cache_status,
          fingerprint: notModifiedResponse.data.fingerprint,
          relationCount: notModifiedResponse.data.relation_count,
          responseHeaders: notModifiedResponse.headers
        });

        return notModifiedResponse;
      }

      const mockResponse = await analysisApiClient.get('/photos/');

      const modifiedResponse = {
        data: {
          ...mockResponse,
          cache_status: 'modified',
          fingerprint: mockFingerprint
        },
        status: 200,
        headers: {
          'X-Photoscope-Resource-Fingerprint': mockFingerprint
        }
      };

      console.log('[0.9.4.9] relationGateway returning mock modified response', {
        cacheStatus: modifiedResponse.data.cache_status,
        fingerprint: modifiedResponse.data.fingerprint,
        hasDetail: Array.isArray(modifiedResponse.data.detail),
        detailCount: modifiedResponse.data.detail?.length || 0,
        responseHeaders: modifiedResponse.headers
      });

      return modifiedResponse;
    }

    console.log('[0.9.4.9] relationGateway calling real API service', {
      params,
      headers
    });

    return relationApiService.listHistory({ params, headers });
  },

  async deleteHistoryGroup(params) {
    return relationApiService.deleteHistoryGroup(params);
  },

  async touchRelation(hashId, options = {}) {
    const response = await relationApiService.touchRelation(hashId, options);
    return normalizeRelationResponse(response);
  },

  async restoreRelation(hashId, relationId) {
    const response = await relationApiService.restoreRelation(hashId, relationId);
    return normalizeRelationResponse(response);
  },

  async createOrUpdateRelation(payload) {
    const response = await relationApiService.createOrUpdateRelation(payload);
    return normalizeRelationResponse(response);
  },

  async deleteImage(hashId, userImageRelationId) {
    return relationApiService.deleteImage(hashId, userImageRelationId);
  },

  async getUserImages({ params = {}, headers = {} } = {}) {
    return relationApiService.getUserImages({ params, headers });
  }
};

export default relationGateway;
